import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ProductSlider = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get('/newProducts.json');
      setProducts(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('商品データの読み込みに失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const visibleProducts = products.filter(product => product.productName !== "ARUMCODE");

  const nextSlide = useCallback(() => {
    if (visibleProducts.length === 0) return;
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % visibleProducts.length);
  }, [visibleProducts]);

  const prevSlide = useCallback(() => {
    if (visibleProducts.length === 0) return;
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + visibleProducts.length) % visibleProducts.length);
  }, [visibleProducts]);

  useEffect(() => {
    if (visibleProducts.length > 0 && !isModalOpen) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
  }, [visibleProducts, nextSlide, isModalOpen]);

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const getProductIndex = (index) => {
    return (index + visibleProducts.length) % visibleProducts.length;
  };

  if (isLoading) {
    return (
      <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center bg-gradient-to-r from-blue-100 to-indigo-100">
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-lg sm:text-xl md:text-2xl text-blue-800 font-semibold"
        >
          商品データを読み込み中...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center bg-gradient-to-r from-blue-100 to-indigo-100">
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-lg sm:text-xl md:text-2xl text-red-600 font-semibold"
        >
          {error}
        </motion.p>
      </div>
    );
  }

  if (visibleProducts.length === 0) {
    return (
      <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center bg-gradient-to-r from-blue-100 to-indigo-100">
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-lg sm:text-xl md:text-2xl text-blue-800 font-semibold"
        >
          表示可能な商品が見つかりません。
        </motion.p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden bg-gradient-to-r from-blue-100 to-indigo-100">
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          className="absolute w-full h-full flex items-center justify-center"
        >
          <div className="flex items-center justify-center w-full">
            <ProductImage
              product={visibleProducts[getProductIndex(currentIndex - 1)]}
              className="hidden md:block w-1/4 h-48 md:h-64 opacity-50 transform scale-75 transition-all duration-300 hover:opacity-75 hover:scale-80"
              onClick={() => prevSlide()}
              isCentral={false}
            />
            <ProductImage
              product={visibleProducts[currentIndex]}
              className="w-full sm:w-3/4 md:w-1/2 h-56 sm:h-72 md:h-96 lg:h-[450px] z-10 mx-0 md:mx-4"
              onClick={(product) =>
                product.productName === "ARUMCODE"
                  ? navigate('/almcode')
                  : product.productName === "はるクール"
                   ? navigate('products/harucool')
                    : setIsModalOpen(true)
              }      
              isCentral={true}
            />
            <ProductImage
              product={visibleProducts[getProductIndex(currentIndex + 1)]}
              className="hidden md:block w-1/4 h-48 md:h-64 opacity-50 transform scale-75 transition-all duration-300 hover:opacity-75 hover:scale-80"
              onClick={() => nextSlide()}
              isCentral={false}
            />
          </div>
        </motion.div>
      </AnimatePresence>

      <button
        onClick={prevSlide}
        className="absolute top-1/2 left-2 sm:left-4 -translate-y-1/2 bg-white bg-opacity-50 rounded-full p-2 sm:p-3 hover:bg-opacity-75 transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="前の商品"
      >
        <ChevronLeft className="text-blue-900 w-4 h-4 sm:w-6 sm:h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute top-1/2 right-2 sm:right-4 -translate-y-1/2 bg-white bg-opacity-50 rounded-full p-2 sm:p-3 hover:bg-opacity-75 transition-all duration-300 z-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="次の商品"
      >
        <ChevronRight className="text-blue-900 w-4 h-4 sm:w-6 sm:h-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {visibleProducts.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              currentIndex === index ? 'bg-blue-600 w-4' : 'bg-blue-300'
            }`}
            aria-label={`商品 ${index + 1} に移動`}
          />
        ))}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <Modal product={visibleProducts[currentIndex]} onClose={() => setIsModalOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

const ProductImage = ({ product, className, onClick, isCentral }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  const handleClick = () => {
    if (product.isCustomLink) {
      navigate(product.linkUrl);
    } else {
      onClick(product);
    }
  };

  return (
    <div className={`relative group ${className}`} onClick={handleClick}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />
      )}
      <motion.img
        src={product.imageUrl}
        alt={product.productName}
        className={`w-full h-full object-contain rounded-lg shadow-lg ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsLoaded(true)}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
        loading="lazy"
      />
      <div className={`absolute inset-0 flex items-center justify-center ${isCentral ? 'opacity-0 group-hover:opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}>
        <span className="bg-white text-blue-900 px-4 py-2 rounded-full shadow-lg text-sm font-semibold flex items-center">
          <ZoomIn className="w-4 h-4 mr-2" />
          {isCentral ? (product.productName === "ARUMCODE" ? 'ARUMCODEページへ' : '詳細を見る') : '選択'}
        </span>
      </div>
    </div>
  );
};

const Modal = ({ product, onClose }) => {
  const navigate = useNavigate();

  const handleAction = () => {
    if (product.isCustomLink) {
      navigate(product.linkUrl);
    } else if (product.catalogUrl) {
      window.open(product.catalogUrl, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full overflow-hidden"
      >
        <div className="relative">
          <img
            src={product.imageUrl}
            alt={product.productName}
            className="w-full h-auto max-h-[50vh] sm:max-h-[70vh] object-contain"
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="閉じる"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-4">{product.productName}</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{product.description || '詳細情報は下のボタンからご確認ください。'}</p>
          <button
            onClick={handleAction}
            className="inline-block bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-full text-sm sm:text-base font-semibold hover:bg-blue-700 transition-colors duration-300"
          >
            {product.productName === "ARUMCODE" ? '詳細を見る' : 'カタログを見る'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProductSlider;